import type { OrgProjectContextOption } from "@arcadeai/ui-kit/org-project-context/types";
import {
  type UseMutationOptions,
  type UseSuspenseQueryOptions,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  getOrganizationsQueryOptions,
  orgProjectKeys,
} from "@/lib/org-project-context/coordinator-query";
import { useScopeKey } from "@/lib/org-project-context/hooks";
import type { Organization } from "../types";
import {
  type UpdateOrganizationVariables,
  updateOrganization,
} from "./update-organization";

function toDashboardOrganizations(
  items: OrgProjectContextOption[]
): Organization[] {
  return items.map((o) => ({
    organization_id: o.id,
    name: o.name,
    description: o.description ?? "",
    created_at: o.createdAt ?? "",
    is_default: o.isDefault,
    // Absent (undefined) means the caller can't edit — fail closed to false.
    can_edit: o.canEdit ?? false,
  }));
}

type UseGetOrganizationsOptions = Omit<
  UseSuspenseQueryOptions<OrgProjectContextOption[], Error, Organization[]>,
  "queryKey" | "queryFn" | "select"
>;

export const useGetOrganizations = (options?: UseGetOrganizationsOptions) => {
  const scopeKey = useScopeKey();
  return useSuspenseQuery({
    ...getOrganizationsQueryOptions(scopeKey),
    select: toDashboardOrganizations,
    ...options,
  });
};

export const useUpdateOrganization = (
  options?: Omit<
    UseMutationOptions<
      Organization,
      Error,
      UpdateOrganizationVariables,
      unknown
    >,
    "mutationFn"
  >
) => {
  const scopeKey = useScopeKey();

  return useMutation({
    mutationFn: updateOrganization,
    meta: {
      invalidates: [orgProjectKeys.organizations(scopeKey)],
    },
    ...options,
  });
};
